/**
 * Created by Ning Jiang on 2021-05-28.
 */

import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {IReelStateEventData} from "../../../core/reel/ReelState";
import {Karen} from "../../Karen";
import {TimelineLite, TweenLite} from "gsap";
import {KarenSymbol} from "../../symbol/KarenSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {ScatterAnimation} from "../../view/ScatterAnimation";
import {DisplayObject} from "pixi.js";
import {KarenLayers, Layers} from "../../view/KarenLayers";
import {KarenSoundManager} from "../../sound/KarenSoundManager";
import {KarenSoundConfig} from "../../sound/KarenSoundConfig";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {KarenGameMode} from "../../server/KarenGameMode";

export class ScatterLandingController extends ReelStopPresentationController {

    protected _scatterCounter: number;
    private readonly _numOfScattersToWin: number = 3;
    private readonly _scattersInReel: boolean[] = [true, true, true, true, true];
    private _timeline: TimelineLite;
    private _bigWinBounce: TweenLite;
    private _isFSUpGrade: boolean = false;

    constructor() {
        super("ScatterLandingController");
    }

    public skip(): void {
        super.skip();
        if (this._timeline instanceof TimelineLite) {
            !this._isFSUpGrade && this._timeline.progress(1);
            // #240 While aborting anticipation reel frame are not getting visible.
            // Karen.reelExpandOverlayView.skipSpineAnimation();
            !this._isFSUpGrade && this.skipScatterAnimations();
        }
    }

    protected onReelBounce(data: IReelStateEventData): Promise<any> {
        let nextReelSize: number = Karen.model.parsedGameData.initialAreaSize[data.reelId + 1];
        if (data.reelId !== 5) {
            if (Karen.model.data.mode !== KarenGameMode.FREESPIN) {
                Karen.xwaysMultiplierView.setXWays(Karen.model.parsedGameData.xWayNumbersOnEachReel[data.reelId + 1].toString());
            } else if (Karen.model.data.mode === KarenGameMode.FREESPIN) {
                Karen.xwaysMultiplierView.calculateXWAYSOnScatterLanding(data.reelId);
            }
        }
        const scatterPosition: number = Karen.model.parsedGameData.scatterPositions![data.reelId];
        if (scatterPosition === -1) {
            return Promise.resolve();
        }
        if (data.reelId !== 5) {
            nextReelSize = Karen.model.parsedGameData.evaluatedArea[data.reelId + 1].length;
        }
        this._timeline = new TimelineLite();
        if (!Karen.model.parsedGameData.boostedBetPlayed) {
            if (this._scatterCounter === 0 && Karen.model.parsedGameData.mode === KarenGameMode.FREESPIN && !Karen.model.parsedGameData.brokeTheBank) {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LAND_FS);
            } else if (this._scatterCounter < 2 && data.reelId === 4) {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LANDING_SOUNDS[4]);
            } else {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LANDING_SOUNDS[this._scatterCounter]);
            }
        } else if (this._scatterCounter === 0 && Karen.model.parsedGameData.mode === KarenGameMode.FREESPIN && !Karen.model.parsedGameData.brokeTheBank) {
            KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LAND_FS);
        } else if ((this._scatterCounter === 0 && data.reelId === 1) || (this._scatterCounter < 2 && data.reelId === 4)) {
            KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LANDING_SOUNDS[4]);
        } else {
            KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_LANDING_SOUNDS[this._scatterCounter]);
        }
        if (this._scatterCounter === 0 && Karen.model.parsedGameData.mode === KarenGameMode.FREESPIN && !Karen.model.parsedGameData.brokeTheBank) {
            this._isFSUpGrade = true;
            this._timeline.add(this.playUpgradeAttentionAnimation(data.reelId, scatterPosition));
            this._timeline.add(() => {
                KarenSoundManager.playFSLoopSound();
            }, "-=0.2");
        } else {
            this._isFSUpGrade = false;
            this._timeline.add(this.playAttentionAnimation(data.reelId, scatterPosition));
        }
        for (let reelIndex: number = data.reelId + 1; reelIndex < 6; reelIndex++) {//#286
            Karen.reelBackground.increaseBounceReelFrame(reelIndex,true);
            Karen.reels[reelIndex].reelView.increaseReelMask(reelIndex,true);
            if (Karen.model.parsedGameData && Karen.model.parsedGameData.mode === GameMode.FREESPIN && reelIndex < 5) {
                Karen.stickyXWView.updateOnUpgrade(reelIndex);
            }
        }
        this._scatterCounter += 1; // To change if more scatter on the one reel.
        if (this._scatterCounter > 2) {
            Karen.reel2Frame.updateMode(GameMode.FREESPIN);
            Karen.background.resetXWaySize();
        }
        if (this.isSkip && !this._isFSUpGrade) {
            this.skip();
        }
        if (Karen.apiPlugin.settings.get(APISetting.FAST_SPIN) && !this._isFSUpGrade) {
            this._timeline.timeScale(2);
        }
        return Promise.resolve(this._timeline);
    }

    protected reset() {
        super.reset();
        this._scatterCounter = 0;
    }

    // override in different scatter winning rules.
    private isPossibleToWin(reelId: number): boolean {
        let counter: number = 0;// how many scatters landed
        let reelsLeft: number = 0; // how many possible reels has scatter left
        Karen.model.parsedGameData.scatterPositions!.forEach((position, positionId) => {
            if (positionId <= reelId && position > -1) {
                counter++;
            }
            if (positionId > reelId && this._scattersInReel[positionId]) {
                reelsLeft++;
            }
        });

        return (counter + reelsLeft) >= this._numOfScattersToWin;
    }

    private playAttentionAnimation(reelId: number, symId: number): TimelineLite {
        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symId);
        let tween: TimelineLite = new TimelineLite();
        const scatterAnim: ScatterAnimation = new ScatterAnimation("scatter");
        this.shakeStage();
        tween.add(scatterAnim.playScatterAnimation("land", reelId, symId));
        return tween;
    }

    private playUpgradeAttentionAnimation(reelId: number, symId: number): TimelineLite {
        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symId);
        let tween: TimelineLite = new TimelineLite();
        const scatterAnim: ScatterAnimation = new ScatterAnimation("scatter");
        this.shakeStage();
        tween.add(scatterAnim.playScatterAnimation("bonus_meal", reelId, symId));
        tween.add(() => {
            Karen.keypadPlugin.setZeroBetSpinCounter(Karen.model.parsedGameData.freespinsLeft);
        }, "-=0.8");
        return tween;
    }

    private skipScatterAnimations(): void {
        let scatterEl: PIXI.spine.Spine[] | undefined = <PIXI.spine.Spine[] | undefined>(KarenLayers.getLayer(Layers.SCATTER_OVERLAY)?.children);
        scatterEl?.forEach((scatterAnimation: PIXI.spine.Spine) => {
            let trackEntry: PIXI.spine.core.TrackEntry = scatterAnimation.state.getCurrent(0);
            if (trackEntry) {
                trackEntry.trackTime = trackEntry.animation.duration;
            }
        });
    }

    private shakeStage(): void {
        if (this.isSkip) {
            this._bigWinBounce && this._bigWinBounce.kill();
            return
        }
        if (this._bigWinBounce && this._bigWinBounce.isActive() && this._bigWinBounce.totalProgress() > 0 && this._bigWinBounce.totalProgress() < 1) {
            this._bigWinBounce.progress(1);
        }
        this._bigWinBounce = TweenLite.to(KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent, 0.2, {
            y: 5,
            ease: Karen.StageShakeBounce,
            onComplete: () => {
                KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent.y = 0;
            }
        });
    }
}
